import type {
  PartnerClient,
  PartnerClientWithSecret
} from '@epilot/sdk/identity'
import { ColumnDef } from '@tanstack/react-table'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { ClientForm } from './client-form'
import { RetiredApplications } from './retired-applications'
import {
  ErrorNotice,
  EPILOT_IDP_PATH,
  SecretDialog,
  StatusBadge
} from './shared'

import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/ui/data-table'
import { config } from '@/configs/api.config'
import { useOidcClients } from '@/services/identity.service'

const columns: ColumnDef<PartnerClient>[] = [
  {
    accessorKey: 'display_name',
    header: 'Application',
    cell: ({ row }) => (
      <Link
        className="underline"
        to={`${EPILOT_IDP_PATH}/${encodeURIComponent(row.original.client_id)}`}
      >
        {row.original.display_name}
      </Link>
    )
  },
  { accessorKey: 'client_id', header: 'Client ID' },
  { accessorKey: 'partner_key', header: 'Partner' },
  { accessorKey: 'environment', header: 'Partner environment' },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => <StatusBadge status={row.original.status} />
  },
  {
    accessorKey: 'updated_at',
    header: 'Updated',
    cell: ({ row }) => new Date(row.original.updated_at).toLocaleString()
  }
]

export const EpilotIdpApplications = () => {
  const query = useOidcClients()
  const navigate = useNavigate()
  const clients = query.data?.pages.flatMap((page) => page.results) ?? []

  return (
    <div className="space-y-4 text-primary">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Partner applications</h1>
          <p className="text-sm text-muted-foreground">
            Applications that sign their users in with Epilot (Epilot as
            identity provider).
          </p>
        </div>
        <Button onClick={() => void query.refetch()} variant="outline">
          Refresh
        </Button>
      </div>
      <p className="text-sm text-muted-foreground">
        Issuer: {config.IDENTITY} ·{' '}
        <a
          className="underline"
          href={`${config.IDENTITY}/.well-known/openid-configuration`}
          rel="noreferrer"
          target="_blank"
        >
          Discovery document
        </a>
      </p>
      <ErrorNotice message={query.error?.message} />
      <DataTable
        allowCreate
        columns={columns}
        data={clients.filter((client) => client.status !== 'retired')}
        defaultSorting={[{ id: 'updated_at', desc: true }]}
        // the search box covers this list; a facet filter per column is noise
        hideColumnFilters
        isLoading={query.isPending}
        onRowClick={(row) =>
          navigate(
            `${EPILOT_IDP_PATH}/${encodeURIComponent(row.original.client_id)}`
          )
        }
        tableConfigName="epilot-idp-applications"
        toolbarProps={{
          filterPlaceholder: 'Search loaded applications…',
          // CSV export and column toggling add nothing for a handful of partner apps.
          exportButtonProps: { className: 'hidden' },
          showViewButton: false
        }}
      />
      {query.hasNextPage && (
        <Button
          loading={query.isFetchingNextPage}
          onClick={() => void query.fetchNextPage()}
          variant="outline"
        >
          Load more applications
        </Button>
      )}
      <RetiredApplications
        clients={clients.filter((client) => client.status === 'retired')}
      />
    </div>
  )
}

export const CreateEpilotIdpApplication = () => {
  const [secret, setSecret] = useState<PartnerClientWithSecret>()
  const navigate = useNavigate()

  return (
    <div className="space-y-4 text-primary">
      <Link className="text-sm underline" to={EPILOT_IDP_PATH}>
        Back to applications
      </Link>
      <h1 className="text-2xl font-semibold">Create partner application</h1>
      <ClientForm onCreated={setSecret} />
      {secret && (
        <SecretDialog
          client={secret}
          onClose={() => {
            const id = secret.client_id

            setSecret(undefined)
            navigate(`${EPILOT_IDP_PATH}/${encodeURIComponent(id)}`)
          }}
        />
      )}
    </div>
  )
}
